'use client';

import { useEffect, useRef, useState } from 'react';
import { Trophy } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import posthog from 'posthog-js';
import { useI18n } from '@/components/I18nProvider';
import { getGameAssetUrl } from '@/lib/assets';
import { placeStyle, type TournamentHistoryEntry } from '@/lib/tournament-history';

type HistoryEvent =
  | 'tournament_history_card_viewed'
  | 'tournament_history_opened'
  | 'tournament_history_event_expanded'
  | 'tournament_cta_clicked'
  | 'tournament_results_link_clicked';

/** PostHog capture for tournament-history usage (same event names as mobile). */
export function trackHistory(event: HistoryEvent, props: Record<string, string | number | boolean>) {
  posthog.capture(event, { platform: 'web', ...props });
}

/** English fallback, used when a page renders outside the [locale] provider. */
const EN = {
  title: 'Tournaments',
  seeAll: 'See all',
  showLess: 'Show less',
  bestFinish: 'Best finish',
  topScorer: 'Top scorer',
  competitor: 'Competitor',
  played: 'Played',
  topTens: 'Top 10s',
  kills: 'Kills',
  best: 'Best',
  recent: 'Recent',
  findNext: 'Find your next tournament',
  emptyTitle: 'No tournaments yet',
  emptyBody: 'Play one and your finishes show up here for every player who views your profile.',
  browse: 'Browse tournaments',
  historyTitle: 'Tournament history',
  historyOf: '@{gamertag}',
  backToProfile: 'Back to profile',
  notTracked: "Results for this event weren't tracked.",
  viewResults: 'View full results',
  viewEvent: 'View event',
  ofField: 'Of {count}',
  rank: 'Rank',
  damage: 'Damage',
  avgPlace: 'Avg place',
  games: 'Games',
  team: 'Team',
  events: '{count} events',
  event: '1 event',
  unavailable: "Tournament history isn't available right now.",
  playedCount: '{count} played',
  bestRank: 'Best #{rank}',
};

export type HistoryCopy = typeof EN;

export function useHistoryCopy(): HistoryCopy {
  const { t } = useI18n();
  return { ...EN, ...((t?.tournamentHistory as Partial<HistoryCopy> | undefined) ?? {}) };
}

export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

/** Animates 0 → value once (ease-out cubic); static under reduced motion. */
export function CountUp({ value, duration = 900 }: { value: number; duration?: number }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(value);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (reduce || value === 0) {
      setShown(value);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    setShown(0);
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [value, duration, reduce]);
  return <span className="tabular-nums">{shown.toLocaleString('en-US')}</span>;
}

export function PlacePill({ rank, label }: { rank: number | null; label: string }) {
  const s = placeStyle(rank);
  return (
    <span
      className="min-w-12 shrink-0 rounded-full border px-2 py-1 text-center font-space-mono text-xs font-bold tabular-nums"
      style={{ color: s.color, borderColor: s.border }}
    >
      {rank ? `#${rank}` : label}
    </span>
  );
}

/** Event cover, falling back to the game icon. */
export function EventArt({ entry, size = 40 }: { entry: TournamentHistoryEntry; size?: number }) {
  const src = entry.coverImage || getGameAssetUrl(entry.gameName);
  return (
    <span
      className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/10"
      style={{ width: size, height: size }}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <Trophy size={size * 0.45} className="text-white/50" />
      )}
    </span>
  );
}

/** Round medal: gold / silver / bronze for a podium finish, neutral otherwise. */
export function Medal({ rank, size = 52 }: { rank: number | null; size?: number }) {
  const s = placeStyle(rank);
  const podium = rank !== null && rank <= 3;
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full border-2 ${podium ? '' : 'border-dashed'}`}
      style={{
        width: size,
        height: size,
        borderColor: podium ? s.color : 'rgba(255,255,255,0.25)',
        backgroundColor: podium ? s.tint : 'rgba(255,255,255,0.04)',
        color: podium ? s.color : 'rgba(255,255,255,0.7)',
      }}
    >
      <Trophy size={Math.round(size / 2)} strokeWidth={1.8} />
    </span>
  );
}
